import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import EmpleadosService from "../../services/EmpleadosService";
import SucursalesService from "../../services/SucursalesService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";

import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Paper,
  Typography,
  Stack,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  OutlinedInput,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const PlantillaEmpleados = () => {
  const usuarioString = sessionStorage.getItem("usuario");
  const currentU = usuarioString ? JSON.parse(usuarioString) : {};

  const [empleados, setEmpleados] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros principales
  const [filtroNombre, setFiltroNombre] = useState("");
  const [filtroSexo, setFiltroSexo] = useState("Todos"); // "Todos", "Femenino", "Masculino"
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("todas");
  const [turnoSeleccionado, setTurnoSeleccionado] = useState("Todos"); // "Todos", "Matutino", "Vespertino"

  useEffect(() => {
    document.title = "Plantilla de Empleados";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resEmp, resSuc] = await Promise.all([
        EmpleadosService.getEmpleados().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
      ]);

      const listaEmp = Array.isArray(resEmp?.data?.empleados)
        ? resEmp.data.empleados
        : Array.isArray(resEmp?.data)
        ? resEmp.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      setEmpleados(listaEmp);
      setSucursales(listaSuc);

      // Sucursal por defecto: usuario actual o todas
      const uSucId = currentU.idSucursal || currentU.id_sucursal || currentU.sucursalId;
      const sucValida = listaSuc.find((s) => String(s.idSucursal || s.id) === String(uSucId));
      if (sucValida) {
        setSucursalSeleccionada(String(sucValida.idSucursal || sucValida.id));
      } else {
        setSucursalSeleccionada("todas");
      }
    } catch (error) {
      console.error("Error al cargar plantilla de empleados:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron obtener los datos para la plantilla de empleados.",
        icon: "error",
        confirmButtonColor: "#006787",
      });
    } finally {
      setLoading(false);
    }
  };

  // Normalizador de datos de empleado
  const getDesgloseEmpleado = (emp) => {
    const nombreCompleto = [emp.nombre, emp.apPaterno, emp.apMaterno]
      .filter(Boolean)
      .join(" ")
      .trim() || emp.nombreEmpleado || "Empleado";

    const direccionCompleta = [
      emp.calle,
      emp.colonia,
      emp.cPostal ? `C.P. ${emp.cPostal}` : "",
      emp.ciudad,
    ]
      .filter(Boolean)
      .join(", ")
      .trim() || emp.direccion || "Sin Dirección";

    const telefono = emp.telefonoCelular || emp.telefonoTrabajo || emp.telefono || emp.celular || "Sin Teléfono";

    const turnoText = emp.turno || emp.turnoN || "Sin Turno";

    const sucObj = sucursales.find((s) => String(s.idSucursal || s.id) === String(emp.idSucursal || emp.sucursalId));
    const sucursalNom = emp.nombreSucursal || sucObj?.nombre || sucObj?.nombreSucursal || "Sin Sucursal";

    const areaNom = emp.nombreDepartamento || emp.departamento || emp.area || emp.puesto || emp.areaLaboral || "General";

    const sexoRaw = `${emp.sexo ?? ""}`.trim().toLowerCase();
    let sexoNorm = "Masculino";
    if (sexoRaw.startsWith("f") || sexoRaw.includes("fem")) {
      sexoNorm = "Femenino";
    }

    return {
      idEmpleado: emp.idEmpleado || emp.id,
      nombreCompleto,
      direccion: direccionCompleta,
      telefono,
      turno: turnoText,
      idSucursal: emp.idSucursal || emp.sucursalId,
      sucursal: sucursalNom,
      area: areaNom,
      sexo: sexoNorm,
    };
  };

  // --- FILTRADO DE EMPLEADOS ---
  const empleadosFiltrados = useMemo(() => {
    return empleados
      .map(getDesgloseEmpleado)
      .filter((emp) => {
        // 1. Filtro de Nombre (Búsqueda en tiempo real)
        if (filtroNombre.trim()) {
          const q = filtroNombre.toLowerCase().trim();
          if (!emp.nombreCompleto.toLowerCase().includes(q)) return false;
        }

        // 2. Filtro de Sexo
        if (filtroSexo !== "Todos") {
          if (emp.sexo.toLowerCase() !== filtroSexo.toLowerCase()) return false;
        }

        // 3. Filtro de Sucursal
        if (sucursalSeleccionada !== "todas") {
          if (String(emp.idSucursal) !== String(sucursalSeleccionada)) return false;
        }

        // 4. Filtro de Turno
        if (turnoSeleccionado !== "Todos") {
          if (!emp.turno.toLowerCase().includes(turnoSeleccionado.toLowerCase())) return false;
        }

        return true;
      });
  }, [empleados, filtroNombre, filtroSexo, sucursalSeleccionada, turnoSeleccionado, sucursales]);

  // --- EXPORTAR A PDF ---
  const handleExportarPDF = async () => {
    if (empleadosFiltrados.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen empleados para exportar con los filtros seleccionados.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF("landscape", "mm", "a4");
        const startY = 12;

    let sucursalActualObj = sucursales.find((s) => String(s.idSucursal || s.id) === String(sucursalSeleccionada)) || sucursales[0] || {};
    const sucursalNomText = (sucursalActualObj.nombre || "LABORATORIO DE ANÁLISIS CLÍNICOS").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;

    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 24);
    } catch (e) {}

    doc.setFillColor(24, 35, 79);
    doc.rect(202, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 242.5, startY + 5, { align: "center" });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 283, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 283, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 283, currY, { align: "right" });

    currY += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(24, 35, 79);
    doc.text("PLANTILLA DE EMPLEADOS", 14, currY);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Total de empleados registrados: ${empleadosFiltrados.length}`, 14, currY + 5);

    let nextY = currY + 12;

    const bodyPDF = empleadosFiltrados.map((emp, idx) => [
      idx + 1,
      emp.nombreCompleto,
      emp.direccion,
      emp.telefono,
      emp.turno,
      emp.sucursal,
      emp.area,
    ]);

    autoTable(doc, {
      startY: nextY,
      head: [["No.", "Nombre", "Dirección", "Teléfono", "Turno", "Sucursal", "Área"]],
      body: bodyPDF,
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5 },
      styles: { fontSize: 8, cellPadding: 2, halign: "left", valign: "middle" },
      columnStyles: {
        0: { width: 15, halign: "center", fontStyle: "bold" },
        1: { fontStyle: "bold" },
        2: { width: 65 },
        3: { width: 30, halign: "center" },
        4: { width: 30, halign: "center" },
        5: { width: 45 },
        6: { width: 35 },
      },
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 200);
      doc.text(`Página ${i} de ${pageCount}`, 283, 200, { align: "right" });
    }

        doc.save(`Plantilla_Empleados.pdf`);
      },
      "Generando reporte de Plantilla de Empleados en PDF...",
      "Procesando documento"
    );
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  PLANTILLA DE EMPLEADOS
                </Typography>
              </Box>

              {/* Filtros del Reporte */}
              <Box sx={{ width: "100%", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros del Reporte
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Nombre y Sexo */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Nombre:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Buscar por nombre..."
                          value={filtroNombre}
                          onChange={(e) => setFiltroNombre(e.target.value)}
                        />
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Sexo:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <RadioGroup
                          row
                          value={filtroSexo}
                          onChange={(e) => setFiltroSexo(e.target.value)}
                        >
                          <FormControlLabel value="Todos" control={<Radio size="small" sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }} />} label="Todos" />
                          <FormControlLabel value="Femenino" control={<Radio size="small" sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }} />} label="Femenino" />
                          <FormControlLabel value="Masculino" control={<Radio size="small" sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }} />} label="Masculino" />
                        </RadioGroup>
                      </TableCell>
                    </TableRow>

                    {/* Fila Sucursal y Turno */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                        >
                          <MenuItem value="todas">Todas las sucursales</MenuItem>
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal || suc.id} value={String(suc.idSucursal || suc.id)}>
                              {suc.nombre || suc.nombreSucursal}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Turno:
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Select
                            fullWidth
                            size="small"
                            value={turnoSeleccionado}
                            onChange={(e) => setTurnoSeleccionado(e.target.value)}
                          >
                            <MenuItem value="Todos">Todos</MenuItem>
                            <MenuItem value="Matutino">Matutino</MenuItem>
                            <MenuItem value="Vespertino">Vespertino</MenuItem>
                          </Select>
                          <IconButton
                            onClick={fetchDatosIniciales}
                            sx={{ backgroundColor: "#006787", color: "white", "&:hover": { backgroundColor: "#014e65" } }}
                            title="Actualizar Datos"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={50} sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <Stack spacing={3} sx={{ width: "100%" }}>
                  {/* TABLA PRINCIPAL DE EMPLEADOS */}
                  <Paper elevation={2} sx={{ p: 0, borderRadius: 3, overflow: "hidden", backgroundColor: "#ffffff", width: "100%" }}>
                    <Table size="small" sx={{ width: "100%", border: "1px solid #e2e8f0" }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#006787" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem", width: "50px" }}>No.</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Nombre</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Dirección</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Teléfono</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Turno</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Sucursal</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.85rem" }}>Área</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {empleadosFiltrados.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#64748b" }}>
                              No se encontraron empleados registrados con los filtros seleccionados.
                            </TableCell>
                          </TableRow>
                        ) : (
                          empleadosFiltrados.map((emp, index) => (
                            <TableRow key={emp.idEmpleado || index} sx={{ "&:hover": { backgroundColor: "#f1f5f9" } }}>
                              <TableCell sx={{ fontWeight: "bold", color: "#64748b", textAlign: "center" }}>{index + 1}</TableCell>
                              <TableCell sx={{ fontWeight: "bold", color: "#1e293b" }}>{emp.nombreCompleto}</TableCell>
                              <TableCell sx={{ color: "#475569", fontSize: "0.85rem" }}>{emp.direccion}</TableCell>
                              <TableCell align="center" sx={{ color: "#006787", fontWeight: "500" }}>{emp.telefono}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "500", color: "#334155" }}>{emp.turno}</TableCell>
                              <TableCell sx={{ fontWeight: "500", color: "#006787" }}>{emp.sucursal}</TableCell>
                              <TableCell sx={{ color: "#475569" }}>{emp.area}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botón flotante lateral derecho para exportar PDF */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          {/* Botón PDF */}
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default PlantillaEmpleados;
